import {
	Component,
	EventEmitter,
	Output,
	AfterViewInit,
	Input,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { ApiService } from 'src/app/services/api.services';
import { DeviceDataRequest } from 'src/app/models/data/device';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { Mode } from 'src/app/models/model';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { AddDeviceGroupNameComponent } from './add-device-group-name/add-device-group-name.component';
import { AddDeviceGroupDevicesComponent } from './add-device-group-devices/add-device-group-devices.component';
import CreateDeviceGroupWithDeviceLimit = Components.Schemas.CreateDeviceGroupWithDeviceLimit;
import { MatDialog } from '@angular/material/dialog';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import { BehaviorSubject } from 'rxjs';
import { AppNavigationHeaderComponent } from '../../components/navigation-header/app-navigation-header.component';
import { AccessHorizontalStepperLabelsComponent } from '../../access-horizontal-stepper/access-horizontal-stepper-labels.component';
import { AddDeviceGroupConfirmationComponent } from './add-device-group-confirmation/add-device-group-confirmation.component';

enum StepperPage {
	name = 0,
	addDevices = 1,
	confirmation = 2,
}

@Component({
	selector: 'app-add-device-group-main-tab',
	templateUrl: './add-device-group-main-tab.component.html',
	styleUrls: [
		'./add-device-group-main-tab.component.scss',
		'../common/stepper.scss',
		'../common/main.scss',
	],
	imports: [
		AppNavigationHeaderComponent,
		AccessHorizontalStepperLabelsComponent,
		AddDeviceGroupNameComponent,
		AddDeviceGroupConfirmationComponent,
		AddDeviceGroupDevicesComponent,
	],
})
export class AddDeviceGroupMainTabComponent implements AfterViewInit {
	addDeviceGroupRequestStatusEvents =
		new BehaviorSubject<AddDeviceGroupStatusEvent>({
			status: AddDeviceGroupRequestStatus.loading,
		});
	@Input() companyId = '';

	@ViewChild(AddDeviceGroupNameComponent)
	addDeviceGroupName?: AddDeviceGroupNameComponent;
	@ViewChild(AddDeviceGroupDevicesComponent)
	addDeviceGroupDevice?: AddDeviceGroupDevicesComponent;
	@ViewChild('stepperRef') stepperRef!: ElementRef;

	editDeviceGroup: EditDeviceGroup = { id: '', name: '', deviceIds: [] };

	stepper!: MatStepper;
	isEditable = false;

	// Access horizontal stepper properties
	steps: string[] = [
		$localize`:@@addDeviceGroupMainTab-name:Name`,
		$localize`:@@addDeviceGroupName-addDevices:Add Devices`,
		$localize`:@@addDeviceGroupName-confirmation:Confirmation`,
	];
	currentStep: number = 0;

	constructor(
		private readonly router: Router,
		private readonly apiService: ApiService,
		private readonly navHistory: NavigationHistoryService,
		private dialog: MatDialog
	) {
		this.stepper = this.stepperRef as unknown as MatStepper;
	}

	async ngAfterViewInit(): Promise<void> {
		if (!this.companyId) {
			const userProfile = await this.apiService.getUserProfile();
			this.companyId = userProfile?.activeRoleDetails?.company?.id ?? '';
		}
	}

	isValid = (): boolean => {
		switch (this.currentStep) {
			case StepperPage.name:
				return this.addDeviceGroupName?.isValid ?? false;
			case StepperPage.addDevices:
				return this.addDeviceGroupDevice?.isValid ?? false;
		}
		return false;
	};

	isHidden = (): boolean => {
		switch (this.currentStep) {
			case StepperPage.confirmation:
				return false;
			default:
				return true;
		}
	};

	createDeviceGroup(): Promise<boolean> {
		this.addDeviceGroupRequestStatusEvents.next({
			status: AddDeviceGroupRequestStatus.loading,
		});

		return this.apiService
			.createDeviceGroup(
				this.companyId,
				this.editDeviceGroup.name,
				this.editDeviceGroup.deviceIds
			)
			.then((result: CreateDeviceGroupWithDeviceLimit) => {
				const { deviceGroupId } = result;

				this.editDeviceGroup.id = deviceGroupId ?? '';
				this.addDeviceGroupRequestStatusEvents.next({
					status: AddDeviceGroupRequestStatus.success,
					data: { deviceGroupId: deviceGroupId ?? '' },
				});

				return true;
			})
			.catch((_) => {
				this.addDeviceGroupRequestStatusEvents.next({
					status: AddDeviceGroupRequestStatus.failure,
				});
				return false;
			});
	}

	readonly getAllDevices: DeviceDataRequest = async (filter) =>
		await this.apiService.getAllDevices(filter);

	movePrevious(): void {
		if (this.currentStep === StepperPage.name) {
			this.navHistory.navigateBack();
		} else {
			this.currentStep--;
		}
	}

	async moveNext(): Promise<void> {
		switch (this.currentStep) {
			case StepperPage.name:
				this.currentStep++;
				break;
			case StepperPage.addDevices:
				const created = await this.createDeviceGroup();
				if (created) {
					this.currentStep++;
				}
				break;
			case StepperPage.confirmation:
				break;
		}
	}

	returnToGroups(): void {
		this.router.navigateByUrl(`/devices?tabIndex=1`);
	}

	viewGroupDetails(): void {
		this.router.navigate(['/device-group', this.editDeviceGroup.id], {
			state: { mode: Mode.edit },
		});
	}
}

export enum AddDeviceGroupRequestStatus {
	loading,
	success,
	failure,
}

export interface AddDeviceGroupStatusEvent {
	status: AddDeviceGroupRequestStatus;
	data?: { deviceGroupId?: string; created?: boolean };
}

export interface EditDeviceGroup {
	id: string;
	name: string;
	deviceIds: string[];
}
